import {
  Alert,
  Button,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  LockIcon,
  TextField,
} from "@strust/ui";
import { css } from "@strust/ui/css";
import { useState, type SyntheticEvent } from "react";
import { submitFields, useFormField } from "../../../shared/form-field.ts";
import { useIntl } from "react-intl";
import { invitationMessages, passwordRevealMessages } from "../messages.ts";

const MIN_PASSWORD_LENGTH = 12;

const FORM = css({ display: "flex", flexDirection: "column", gap: "4" });
const FULL = css({ width: "full" });
const RULES = css({
  listStyle: "none",
  m: "0",
  p: "0",
  display: "flex",
  flexDirection: "column",
  gap: "1.5",
  textStyle: "caption",
});
const RULE_MET = css({
  display: "flex",
  alignItems: "center",
  gap: "2",
  color: "ok.fg",
  "& svg": { width: "icon.md", height: "icon.md", flexShrink: "0" },
});
const RULE_FAILED = css({
  display: "flex",
  alignItems: "center",
  gap: "2",
  color: "deny.fg",
  "& svg": { width: "icon.md", height: "icon.md", flexShrink: "0" },
});
const RULE_OPEN = css({
  display: "flex",
  alignItems: "center",
  gap: "2",
  color: "text.2",
  "& svg": { width: "icon.md", height: "icon.md", flexShrink: "0" },
});
const SR_ONLY = css({ srOnly: true });

export interface InvitationFormProps {
  readonly onSubmit: (password: string) => void;
  readonly pending: boolean;
  /** Server-side refusal (weak password, expired, ...), already translated. */
  readonly error?: string | undefined;
  /** The server refused the last submitted password as common or breached (the rule is then failed). */
  readonly commonRefused?: boolean;
  /** Label of the primary button (default: the invitation's). */
  readonly submitLabel?: string;
}

type RuleState = "met" | "failed" | "open";

/**
 * Password choice for an invitation: the 12-character rule and the match are shown live in the
 * checklist and the primary button stays disabled until both hold. The server is the authority for
 * the common-password rule (its list is not shipped to the browser): that rule stays open until the
 * server refuses the submitted password, then it shows as failed.
 */
export function InvitationForm({
  onSubmit,
  pending,
  error,
  commonRefused,
  submitLabel,
}: InvitationFormProps) {
  const intl = useIntl();
  const [submitted, setSubmitted] = useState<string | undefined>(undefined);
  // The messages show once the user leaves a field or submits, never while typing the confirmation.
  const password = useFormField({
    validate: (value) =>
      value !== "" && value.length < MIN_PASSWORD_LENGTH
        ? intl.formatMessage(invitationMessages.tooShort)
        : undefined,
  });
  const longEnough = password.value.length >= MIN_PASSWORD_LENGTH;
  const confirm = useFormField({
    validate: (value) =>
      value !== "" && longEnough && value !== password.value
        ? intl.formatMessage(invitationMessages.mismatch)
        : undefined,
  });
  const matches = password.value !== "" && password.value === confirm.value;
  const common: RuleState =
    commonRefused === true && password.value === submitted ? "failed" : "open";

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!submitFields(password, confirm) || !longEnough || !matches) return;
    setSubmitted(password.value);
    // The password stays (not reset): a refusal by the server is fixed by editing it.
    onSubmit(password.value);
  };

  const reveal = {
    show: intl.formatMessage(passwordRevealMessages.show),
    hide: intl.formatMessage(passwordRevealMessages.hide),
  };
  const rule = (state: RuleState, label: string) => (
    <li className={state === "met" ? RULE_MET : state === "failed" ? RULE_FAILED : RULE_OPEN}>
      {state === "met" ? (
        <CircleCheckIcon aria-hidden="true" />
      ) : state === "failed" ? (
        <CircleXIcon aria-hidden="true" />
      ) : (
        <CircleDashedIcon aria-hidden="true" />
      )}
      <span>{label}</span>
      <span className={SR_ONLY}>
        {intl.formatMessage(
          state === "met"
            ? invitationMessages.ruleMet
            : state === "failed"
              ? invitationMessages.ruleFailed
              : invitationMessages.ruleUnmet,
        )}
      </span>
    </li>
  );

  return (
    <form onSubmit={submit} noValidate className={FORM}>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <TextField
        label={intl.formatMessage(invitationMessages.password)}
        type="password"
        name="new-password"
        autoComplete="new-password"
        leadingIcon={<LockIcon />}
        revealLabels={reveal}
        hint={intl.formatMessage(invitationMessages.passwordRule)}
        {...password.inputProps}
      />
      <TextField
        label={intl.formatMessage(invitationMessages.confirm)}
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        leadingIcon={<LockIcon />}
        revealLabels={reveal}
        {...confirm.inputProps}
      />
      <ul className={RULES} aria-label={intl.formatMessage(invitationMessages.rulesLabel)}>
        {rule(longEnough ? "met" : "open", intl.formatMessage(invitationMessages.ruleLength))}
        {rule(common, intl.formatMessage(invitationMessages.ruleCommon))}
        {rule(matches ? "met" : "open", intl.formatMessage(invitationMessages.ruleMatch))}
      </ul>
      <Button
        type="submit"
        className={FULL}
        visual="primary"
        disabled={!longEnough || !matches}
        loading={pending}
      >
        {submitLabel ?? intl.formatMessage(invitationMessages.submit)}
      </Button>
    </form>
  );
}
